import { Link, createFileRoute } from '@tanstack/react-router';
import { GENRE_LANDING_BY_SLUG } from '~/content/genres';
import { SITE_URL, buildHead } from '~/seo/site';

export const Route = createFileRoute('/beat-battles/$genre')({
  head: ({ params }) => {
    const g = GENRE_LANDING_BY_SLUG[params.genre];
    if (!g) {
      return buildHead({
        title: 'Genre not found',
        description: 'No beat-battle landing for this genre.',
        path: `/beat-battles/${params.genre}`,
        noindex: true,
      });
    }
    const head = buildHead({
      title: `${g.name} Beat Battles - Compete Online`,
      description: g.description,
      path: `/beat-battles/${g.slug}`,
    });
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: g.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Beat Battles',
          item: `${SITE_URL}/beat-battles`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: g.name,
          item: `${SITE_URL}/beat-battles/${g.slug}`,
        },
      ],
    };
    return {
      ...head,
      meta: [...head.meta, { 'script:ld+json': faqSchema }, { 'script:ld+json': breadcrumbSchema }],
    };
  },
  component: GenreLanding,
});

function GenreLanding() {
  const { genre } = Route.useParams();
  const g = GENRE_LANDING_BY_SLUG[genre];
  if (!g) {
    return (
      <section className="mx-auto max-w-2xl px-4 py-20 text-center space-y-4">
        <h1 className="text-2xl font-bold">Genre not found</h1>
        <Link to="/genres" className="underline">
          See all genres
        </Link>
      </section>
    );
  }
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 space-y-10">
      <header className="space-y-3">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          {g.bpmRange}
        </p>
        <h1 className="text-3xl font-bold tracking-tight">{g.name} Beat Battles</h1>
        <p className="text-lg text-muted-foreground leading-relaxed">{g.intro}</p>
        <div className="flex flex-wrap gap-3 pt-2">
          <Link
            to="/play"
            className="rounded-md bg-primary px-4 py-2 font-mono text-[12px] font-bold uppercase tracking-[0.18em] text-primary-foreground"
          >
            Play {g.name.toLowerCase()} now
          </Link>
          <Link
            to="/feed"
            search={{ genre: g.slug }}
            className="rounded-md border border-border px-4 py-2 font-mono text-[12px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
          >
            Browse {g.name.toLowerCase()} feed
          </Link>
        </div>
      </header>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">What defines a {g.name.toLowerCase()} beat battle</h2>
        <ul className="list-disc pl-6 space-y-1.5 text-foreground/90">
          {g.signature.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">Match rules</h2>
        <ul className="list-disc pl-6 space-y-1.5 text-foreground/90">
          {g.rules.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold">Frequently asked</h2>
        <div className="space-y-2">
          {g.faqs.map((f) => (
            <details
              key={f.q}
              className="group rounded-md border border-border bg-card open:bg-muted/40"
            >
              <summary className="cursor-pointer list-none px-4 py-3 text-sm font-medium flex items-center justify-between">
                <span>{f.q}</span>
                <span className="text-muted-foreground transition-transform group-open:rotate-90">
                  ›
                </span>
              </summary>
              <div className="px-4 pb-4 pt-1 text-sm text-muted-foreground leading-relaxed">
                {f.a}
              </div>
            </details>
          ))}
        </div>
      </section>

      <section className="rounded-md border border-primary/30 bg-primary/5 px-5 py-4 text-sm">
        <p>
          Ready to enter your first {g.name.toLowerCase()} battle?{' '}
          <Link to="/play" className="underline decoration-primary underline-offset-2">
            Pick a mode and start
          </Link>
          . Free to play.
        </p>
      </section>
    </article>
  );
}
